import React, { useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ApplicationStatus } from '../../types/wizard';
import { getV2WizardRedirect, mapApplicantStepToWizardStep } from '../../utils/wizardSteps';
import { courierApiUrl, withApplicantBasePath } from '../../config';
import {
  clearApplicantV2Session,
  getApplicantV2ErrorMessage,
  getApplicantV2State,
  loadApplicantV2Session,
  toApplicationStatus,
} from '../../utils/applicantV2';

interface WizardRouteProps {
  children: (applicationStatus: ApplicationStatus) => React.ReactNode;
}

const WizardRoute: React.FC<WizardRouteProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applicationStatus, setApplicationStatus] = useState<ApplicationStatus | null>(null);

  const fetchApplicationStatus = useCallback(async () => {
    try {
      const v2Session = loadApplicantV2Session();
      if (v2Session) {
        const state = await getApplicantV2State(v2Session);
        const status = toApplicationStatus(state);
        const redirectPath = getV2WizardRedirect(location.pathname, status.currentStepId);
        if (redirectPath) {
          navigate(redirectPath, { replace: true });
          return;
        }
        setApplicationStatus(status);
        setLoading(false);
        return;
      }

      if (!token || token === 'undefined') {
        setError('Your application session has expired. Request a new verification code to continue.');
        setLoading(false);
        return;
      }

      // Keep signed legacy links working while the V2 applicant flow rolls out.
      const response = await axios.get(
        courierApiUrl('/application/status'),
        { params: { token } }
      );

      if (response.data.success) {
        const data = response.data.applicationStatus;

        // Map backend data to ApplicationStatus type
        const status: ApplicationStatus = {
          email: data.email || '',
          name: data.name || data.Name || '',
          currentStepId: mapApplicantStepToWizardStep(data.ApplicantStep || data.applicantStep || 'Applied'),
          applicantStep: data.ApplicantStep || data.applicantStep || 'Applied',
          deliveryArea: data.DeliveryArea || data.deliveryArea || '',
          documentToken: data.DocumentToken || data.documentToken || token,
          rejectionReason: data.rejectionReason,
          interviewDate: data.interviewDate,
          steps: [] // Will be calculated by WizardContainer
        };

        setApplicationStatus(status);
        setLoading(false);
      } else {
        setError(response.data.error || 'Failed to load application status.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error fetching application status:', err);
      if (loadApplicantV2Session() && err.response?.status === 401) {
        clearApplicantV2Session();
      }
      setError(getApplicantV2ErrorMessage(
        err,
        'Failed to load your application. Request a new verification code and try again.'
      ));
      setLoading(false);
    }
  }, [location.pathname, navigate, token]);

  useEffect(() => {
    fetchApplicationStatus();
  }, [fetchApplicationStatus]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your application...</p>
        </div>
      </div>
    );
  }

  if (error || !applicationStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="card p-8 max-w-md">
          <div className="text-alert-red text-4xl mb-4 text-center">⚠️</div>
          <h1 className="text-2xl font-bold text-charcoal mb-4 text-center">
            Unable to Load Application
          </h1>
          <p className="text-gray-600 mb-6 text-center">
            {error}
          </p>
          <a
            href={withApplicantBasePath('/')}
            className="btn-primary w-full text-center block"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children(applicationStatus)}</>;
};

export default WizardRoute;
